import { z } from 'zod';
import { meterSchema } from './vendor-plans.js';
import { scopeSchema } from './scopes.js';
/**
 * Tool manifests (Constitution §6).
 *
 * "Default-deny: a call is refused unless a signed manifest grants it. Tools
 * are held to exactly their declared surface."
 *
 * The manifest is the ONLY place a destination exists. A model proposes a
 * tool id and arguments; it never supplies a URL, a method, or a host. That
 * is what makes "a jailbreak cannot invent a destination policy never
 * sanctioned" a property of the system rather than a hope.
 */
/** Operation tiers (Constitution §17). */
export const tierSchema = z.enum(['routine', 'sensitive', 'critical']);
/**
 * The surface a tool is held to. Anything not described here is refused, so
 * an under-specified manifest fails closed rather than open.
 */
/** Headers a manifest may never fix: credentials, and what the transport owns. */
export const FORBIDDEN_FIXED_HEADERS = new Set([
    'authorization',
    'proxy-authorization',
    'cookie',
    'host',
    'content-length',
    'x-goog-api-key',
    'x-api-key',
]);
export const surfaceSchema = z.object({
    /** How the tool is reached. */
    transport: z.enum(['http', 'mcp']),
    /**
     * The exact origin the gateway will dispatch to. One origin per manifest:
     * a tool that needs two is two tools, each separately granted.
     */
    origin: z.string().url(),
    /** HTTP methods permitted. Empty means none, not all. */
    methods: z.array(z.enum(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'])).min(1),
    /**
     * Path templates permitted, e.g. `/v1/orders` or `/v1/orders/{id}`.
     * Matched structurally, never by prefix — a prefix match on `/v1/orders`
     * would also admit `/v1/orders-admin`.
     */
    paths: z.array(z.string().startsWith('/')).min(1),
    /**
     * Query parameter names this tool may carry. An allow-list.
     *
     * Absent means **none**, matching `methods` — empty is none, not all.
     * That is not pedantry: `pathMatches` strips a query string before
     * comparing segments, so until this existed the query was the one part
     * of a destination nothing checked. A caller granted
     * `GET /drive/v3/files/{fileId}` could append `?alt=media` and turn a
     * metadata read into a download of the file's contents — a different
     * capability, from a manifest that never offered it.
     *
     * Optional rather than defaulted so an existing manifest's canonical
     * form is unchanged and its signature still verifies.
     */
    query: z.array(z.string().regex(/^[A-Za-z][A-Za-z0-9_.]*$/)).optional(),
    /** Optional signed value bounds. Each named parameter is required exactly once. */
    queryValues: z
        .record(z.string().regex(/^[A-Za-z][A-Za-z0-9_.]*$/), z.array(z.string()).min(1).max(10))
        .optional(),
    /**
     * Headers sent on every call, as signed — never as a caller supplied them.
     *
     * Places needs `X-Goog-FieldMask`, and a mask has commas, which the query
     * allow-list refuses; more to the point, a mask a caller chose would be a
     * caller choosing the billing tier and what Google returns. So it is part
     * of the surface. A credential is never one: `authorization` and the
     * vendor key headers are what a connection or a minted secret supplies,
     * and a header that could carry a key in a manifest would be a static key
     * in the ledger. Optional and undefaulted, so every existing signature is
     * unchanged.
     */
    fixedHeaders: z
        .record(z
        .string()
        .regex(/^[A-Za-z0-9-]+$/)
        .refine((h) => !FORBIDDEN_FIXED_HEADERS.has(h.toLowerCase()), {
        message: 'a credential, the host or the body length is never a fixed header',
    }), z
        .string()
        .min(1)
        .max(1024)
        .regex(/^[^\r\n]*$/))
        .optional(),
    /** MCP tool names permitted, when transport is mcp. */
    tools: z.array(z.string()).optional(),
    /** Maximum request body size the gateway will forward. */
    maxBodyBytes: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).default(1_000_000),
});
export const rateLimitSchema = z.object({
    requestsPerMinute: z.number().int().positive(),
    burst: z.number().int().positive(),
    /**
     * A daily ceiling, where the surface behind the tool has one (§19,
     * amendment 0003). A directory or a social network limits a person to so
     * many lookups a day; an agent that exceeded that would be banned, and one
     * that reaches it and stops is indistinguishable from a diligent person.
     *
     * Optional and undefaulted, so every existing signature is unchanged and
     * a ceiling is something a manifest states rather than inherits.
     */
    requestsPerDay: z.number().int().positive().optional(),
    /**
     * A monthly allowance, where the vendor meters by the month — Hunter's
     * free plan is fifty credits a month, Place Details a thousand calls
     * (ADR-0078). A daily ceiling cannot say it: thirty days at a person's
     * pace can exceed a month's allowance. Optional and undefaulted, the move
     * `requestsPerDay` made.
     */
    requestsPerMonth: z.number().int().positive().optional(),
    /**
     * Whether the daily ceiling is counted per person acted as (ADR-0094).
     * A tool that reads a person's own calendar or drive under their consent
     * has a person's pace per person, and one budget shared across a company
     * would stop everyone once it grew past a few dozen people — or let one
     * runaway person spend everyone's. Optional and undefaulted, as above.
     */
    perSubject: z.boolean().optional(),
});
export const manifestSchema = z.object({
    /** Stable identifier, `<department>.<tool_name>`. */
    id: z.string().regex(/^[a-z][a-z_]*\.[a-z][a-z_0-9]*$/),
    /** Bumped on any change. The signature covers this, so a rollback is visible. */
    version: z.number().int().positive(),
    description: z.string().min(1).max(500),
    /**
     * Departments whose agents may be granted this tool. A tool reachable by
     * every department is a tool that breaks §6's isolation, so this is never
     * empty and there is no wildcard.
     */
    departments: z.array(z.string().regex(/^[a-z][a-z_]*$/)).min(1),
    /** Scopes an agent must hold to call it. */
    requiredScopes: z.array(scopeSchema).min(1),
    surface: surfaceSchema,
    /**
     * Hosts this tool may cause the gateway to contact. Declared so that an
     * exfiltration path shows up in the manifest rather than at runtime.
     */
    egress: z.array(z.string()).default([]),
    rateLimit: rateLimitSchema,
    /**
     * What one call spends at the vendor, in the vendor's unit (ADR-0095).
     * Signed with the rest, so what a call costs is a decision in review and
     * not something a caller can say about itself. Optional and undefaulted:
     * a tool that costs nothing states nothing, and every existing signature
     * is unchanged.
     */
    meters: z.array(meterSchema).min(1).max(4).optional(),
    /**
     * Secret references, never secrets. The gateway resolves these to
     * short-lived credentials at call time (§6).
     */
    secrets: z.array(z.string()).default([]),
    /** Constitution §17. `critical` additionally requires a supervision token. */
    tier: tierSchema,
    /**
     * Whether a `sensitive` operation may be performed by a granted agent
     * under a dual-control record, rather than only by the
     * privileged-operations agent (§17, amendment 0002).
     *
     * Opt-in per manifest, and defaulted to `false` on purpose. The amendment
     * widened who *may* perform a sensitive operation; it did not widen which
     * operations are open to that. A tool gets this because someone wrote it
     * into the manifest and signed it, which is a decision that shows up in
     * review — not because of what tier it happens to carry.
     *
     * It does nothing at `routine` (no restriction to lift) and nothing at
     * `critical` (unchanged by the amendment).
     */
    dualControl: z.boolean().default(false),
    /** Conditions that force a human into the loop even below critical tier. */
    hitlTriggers: z.array(z.string()).default([]),
    issuedAt: z.string().datetime(),
    /** Manifests expire, so a forgotten grant does not live forever. */
    expiresAt: z.string().datetime(),
});
/** A manifest plus its detached signature, as stored and distributed. */
export const signedManifestSchema = z.object({
    manifest: manifestSchema,
    /** Ed25519 detached signature over the canonical serialisation, base64url. */
    signature: z.string().min(1),
    /** Which signing key produced it, so keys can be rotated without ambiguity. */
    keyId: z.string().min(1),
});
/**
 * Where a published manifest lives in the gateway's key-value namespace.
 *
 * Here rather than in either side, because the gateway and the publisher are
 * deployed separately and a key shape agreed by convention is a key shape that
 * eventually disagrees. When it did, the symptom would be a default-deny
 * refusal for a tool that had in fact been granted — a security control
 * failing safe and looking like a bug.
 */
export const MANIFEST_KEY_PREFIX = 'manifest:';
export function manifestKey(toolId) {
    return `${MANIFEST_KEY_PREFIX}${toolId}`;
}
/**
 * Canonical serialisation for signing.
 *
 * Keys are sorted recursively so that re-ordering fields — which JSON
 * considers equivalent — cannot produce a different signature, and so that a
 * manifest signed by one implementation verifies under another.
 */
export function canonicalise(value) {
    return JSON.stringify(sortDeep(value));
}
function sortDeep(value) {
    if (Array.isArray(value))
        return value.map(sortDeep);
    if (value && typeof value === 'object') {
        return Object.fromEntries(Object.entries(value)
            .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
            .map(([k, v]) => [k, sortDeep(v)]));
    }
    return value;
}
//# sourceMappingURL=manifest.js.map